<template>
  <div
    class="flex-layout"
    :class="[
      align ? `align-${align}` : '',
      justify ? `justify-${justify}` : '',
      gap && `gap-${gap}`,
      {
        wrap: wrap,
        flush: flush,
        fill: fill,
        [direction]: direction,
      },
    ]"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
export type FlexGap = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type FlexAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type FlexJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
export type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';

withDefaults(
  defineProps<{
    gap?: FlexGap;
    align?: FlexAlign;
    justify?: FlexJustify;
    wrap?: boolean;
    fill?: boolean;
    direction?: FlexDirection;
    flush?: boolean;
  }>(),
  {
    gap: 'sm',
    align: 'stretch',
    justify: 'start',
    wrap: false,
    fill: false,
    direction: 'row',
    flush: false,
  },
);
</script>

<style scoped>
.flex-layout {
  display: flex;
  min-width: 0;
  width: 100%;
  gap: var(--flex-layout-gap);
  margin-bottom: var(--flex-layout-gap);
  &:last-child {
    margin-bottom: 0;
  }
  &.flush {
    margin-bottom: 0;
  }
  &.fill {
    height: 100%;
  }

  &.row {
    flex-direction: row;
  }
  &.row-reverse {
    flex-direction: row-reverse;
  }
  &.column {
    flex-direction: column;
  }
  &.column-reverse {
    flex-direction: column-reverse;
  }

  &.column > *:not(:last-child) {
    margin-bottom: 0 !important;
  }

  &.wrap {
    flex-wrap: wrap;
  }

  &.align-start {
    align-items: flex-start;
  }

  &.align-center {
    align-items: center;
  }

  &.align-end {
    align-items: flex-end;
  }

  &.align-stretch {
    align-items: stretch;
  }

  &.align-baseline {
    align-items: baseline;
  }

  &.justify-start {
    justify-content: flex-start;
  }

  &.justify-center {
    justify-content: center;
  }

  &.justify-end {
    justify-content: flex-end;
  }

  &.justify-between {
    justify-content: space-between;
  }

  &.justify-around {
    justify-content: space-around;
  }

  &.justify-evenly {
    justify-content: space-evenly;
  }

  &.gap-none {
    --flex-layout-gap: 0px;
  }

  &.gap-xs {
    --flex-layout-gap: var(--gap-xs);
  }

  &.gap-sm {
    --flex-layout-gap: var(--gap-sm);
  }

  &.gap-md {
    --flex-layout-gap: var(--gap-md);
  }

  &.gap-lg {
    --flex-layout-gap: var(--gap-lg);
  }

  &.gap-xl {
    --flex-layout-gap: var(--gap-xl);
  }
}
</style>
